import { useCallback, useEffect, useState } from 'react';

import { ApiError } from '@/api/client';
import { progress } from '@/api/endpoints';
import { useSession } from '@/store/session';
import { Icon } from '@/components/ui/Icon';
import {
  Button, Empty, ErrorNote, Loading, Meter, Panel, ScreenBody, ScreenHeader,
} from '@/components/ui/primitives';

type Mission = Awaited<ReturnType<typeof progress.missions>>['results'][number];

/** Daily and weekly missions. Progress is server-derived, so no local counters. */
export function Missions({ onBack }: { onBack: () => void }) {
  const [rows, setRows] = useState<Mission[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const refresh = useSession((s) => s.refresh);

  const load = useCallback(() => { void progress.missions().then((r) => setRows(r.results)); }, []);
  useEffect(load, [load]);

  async function claim(key: string) {
    setBusy(key); setError(null);
    try {
      await progress.claimMission(key);
      load();
      void refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not claim that.');
    } finally { setBusy(null); }
  }

  if (!rows) return <Loading />;

  const groups: [string, Mission[]][] = [
    ['DAILY', rows.filter((r) => r.scope === 'daily')],
    ['WEEKLY', rows.filter((r) => r.scope === 'weekly')],
  ];

  return (
    <ScreenBody>
      <ScreenHeader icon={<Icon name="target" size={24} />} title="Missions" onBack={onBack} />
      {error && <ErrorNote>{error}</ErrorNote>}

      {groups.map(([label, list]) => list.length > 0 && (
        <Panel key={label} title={label} className="mb-3">
          {list.map((m) => {
            const pct = m.target > 0 ? (m.progress / m.target) * 100 : 0;
            return (
              <div key={m.key} className="border-t border-[var(--hairline)] py-3 first:border-t-0">
                <div className="flex items-start gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border
                                   border-[var(--hairline)] bg-black/35 text-lg" aria-hidden>
                    {m.completed ? <Icon name="check" size={20} className="text-[var(--ok)]" /> : <Icon name="target" size={20} className="text-torch-core" />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-bold">{m.name}</p>
                    <p className="text-[11px] text-ink-dim">{m.description}</p>
                    <div className="mt-1.5"><Meter pct={pct} label={`${m.name} progress`} /></div>
                    <p className="mt-1 text-[10px] text-ink-dim tabular">
                      {m.progress.toLocaleString('en-US')} / {m.target.toLocaleString('en-US')}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="flex items-center justify-end gap-1 text-xs font-bold text-coin tabular">
                      <Icon name="coin" size={13} />
                      {m.reward_coins.toLocaleString('en-US')}
                    </p>
                    {Number(m.reward_kyat) > 0 && (
                      <p className="flex items-center justify-end gap-1 text-xs font-bold text-kyat-glow tabular">
                        <Icon name="kyat" size={13} />
                        {m.reward_kyat}
                      </p>
                    )}
                  </div>
                </div>
                {m.claimable && (
                  <Button variant="primary" className="mt-2 w-full" busy={busy === m.key}
                          onClick={() => void claim(m.key)}>
                    Claim reward
                  </Button>
                )}
              </div>
            );
          })}
        </Panel>
      ))}

      {rows.length === 0 && <Panel><Empty>No missions assigned yet.</Empty></Panel>}
    </ScreenBody>
  );
}
